<template>
    <div class="mt--10">
        <div class="client__notes">
            <template v-if="!activeIntegrations.length">
                <EmptyPage 
                    v-if="accountPlan === 'free'"
                    state="empty_custom" 
                    icon-name="money" 
                    type="data"
                    btn-state="custom"
                    title="This is a paid feature"
                    subtitle="Upgrade to a paid plan to get access to app integrations" 
                >
                    <template #button>
                        <BaseButton 
                            label="Go to account settings" 
                            :show-spinner="false"
                            @submit="goToAccountSettings"
                        />
                    </template>
                </EmptyPage>
                
                <EmptyPage 
                    v-else
                    state="empty_custom" 
                    icon-name="store-alt" 
                    type="data"
                    title="You have no active integrations"
                    subtitle="Go to the integrations directory to view all available integrations" 
                />
            </template>
                    
            <template v-else>
                <div class="">                        
                    <template v-if="pageState.loading">
                        <PageSpinner />
                    </template>
                        
                    <EmptyPage v-else-if="pageState.err" state="error" @action="handleFetchIntegrations" />
                                            
                    <template v-else>
                        <div class="mt--20 integration__active__wrap">
                            <div
                                v-for="(item, index) in activeIntegrations" 
                                :key="index" class="settings__integrations__card__item pointer h--100"
                                :class="{ 'disabled': selectedIntegrationName === item.name && loading }"
                            >
                                <BaseCard
                                    aria-label="inner content shows on hover"
                                    shadow hover
                                    class="overflow-visible h--100 card-solid-light"
                                    @click="$emit('open-modal', item)"
                                >
                                    <template #card-body>
                                        <div class="outer__wrap">
                                            <div class="flex align-items-baseline w-100 justify-content-between">
                                                <div class="flex align-items-center w-100 mt--3">
                                                    <IntegrationLogos v-if="item.key" :name="item.key" />
                                                    <div class="ml--10 flex-2">
                                                        <div class="flex align-items-baseline w-100 justify-content-between">
                                                            <p class="text--capitalize text--sm text--semi--bold text-gray-800">
                                                                {{ item.name }}
                                                            </p> 
                                                        </div>
                                                        <p class="text--capitalize text--xs text-gray-600 mt--5">
                                                            {{ item.description }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </BaseCard>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { EmptyPage, PageSpinner, BaseButton, BaseCard, IntegrationLogos } from "@/components/ui/index";
import { integrationProvidersLogos } from "@/utils/enums";

export default {
    name: "ActiveIntegrations",

    components: {
        EmptyPage,
        PageSpinner,
        BaseButton,
        BaseCard,
        IntegrationLogos,
    },

    props: {
        loading: {
            type: Boolean,
            default: false,
        },

        selectedIntegrationName: {
            type: String,
            default: "",
        },
    },

    emits: ["disable", "open-modal"],

    data() {
        return {
            pageState: {
                loading: false,
                err: false,
                errMsg: "",
            },
            integrationProvidersLogos,
        };
    },

    computed: {
        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        userIntegrations() {
            return this.$store.getters["integration/integrations"] || [];
        },

        appIntegrations() {
            return this.$store.getters["config/integrations"] || [];
        },

        userIntegrationsMap() {
            const newMap = new Map();

            for (let i = 0; i < this.userIntegrations.length; i++) {
                const item = this.userIntegrations[i];
                newMap.set(item.key, {
                    _id: item._id,
                    enabled: item.enabled,
                });
            } 

            return newMap;
        },

        userProfile() {
            return this.$store.getters["auth/profile"];
        },

        activeIntegrations() {
            const result = [];

            for (let i = 0; i < this.appIntegrations.length; i++) {
                const item = this.appIntegrations[i];
                const mappedItem = this.userIntegrationsMap.get(item?.key);

                if (mappedItem) {
                    result.push({ ...item, ...mappedItem });
                }
            }

            return result;
        },
    },

    async mounted() {     
        if (this.accountPlan !== "free") {
            if (!this.userIntegrations.length) {
                await this.handleFetchIntegrations({ refresh: true });
            }
        }
    },

    methods: {
        goToAccountSettings() {
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        async handleFetchIntegrations(params = {}) {
            try {
                this.pageState.loading = true;
                this.pageState.errMsg = "";
                this.pageState.err = false;

                await this.$store.dispatch("integration/fetchUserIntegrations", { refresh: params?.refresh, params });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.pageState.err = true;
                this.pageState.errMsg = errMsg;
            } finally {
                this.pageState.loading = false;
            }
        },
    },
};
</script>